import { Box, useTheme } from '@mui/material';

/**
 * v4 switch (DESIGN_SYSTEM.md section 8.7, #1377). The only toggle in the
 * system; binary settings only (theme, tips). Small size only: 30x18 track
 * (theme.ds.size), 14px thumb. Checked: interactive track + interactiveOn
 * thumb. Unchecked: borderStrong track + overlay thumb. Disabled dims via
 * alpha, never a hue swap. The label lives outside (see SwitchRow) and names
 * the setting in sentence case without baking in the state.
 *
 * @param {object} props
 * @param {boolean} [props.checked]
 * @param {function} [props.onChange]   called with the next boolean value
 * @param {boolean} [props.disabled]
 * @param {string} [props.ariaLabel]    required when no visible label wraps it
 * @param {object} [props.sx]           extra sx merged onto the track
 */
export const Switch = ({ checked = false, onChange, disabled = false, ariaLabel, sx, ...rest }) => {
  const theme = useTheme();
  const { ds, palette } = theme;

  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) onChange(!checked);
      }}
      sx={{
        width: ds.size.switchW,
        height: ds.size.switchH,
        borderRadius: `${ds.size.switchH / 2}px`,
        backgroundColor: checked ? palette.interactive : palette.borderStrong,
        border: 'none',
        padding: 0,
        position: 'relative',
        flexShrink: 0,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
        ...sx,
      }}
      {...rest}
    >
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          width: 14,
          height: 14,
          borderRadius: '50%',
          backgroundColor: checked ? palette.interactiveOn : palette.surface.overlay,
          transform: checked ? 'translateX(12px)' : 'translateX(0)',
          transition: `transform ${ds.motion.fast} ${ds.motion.easeOut}, background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
        }}
      />
    </Box>
  );
};

/**
 * v4 switch row (section 8.7): the whole row is the interactive target
 * (role="menuitemcheckbox"); the embedded Switch is presentational
 * (pointerEvents none). Toggling a SwitchRow inside a menu must not close the
 * menu - callers own that contract. `indent` left-pads nested menu rows.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label   setting name, sentence case, stateless
 * @param {boolean} [props.checked]
 * @param {function} [props.onChange]     called with the next boolean value
 * @param {boolean} [props.disabled]
 * @param {number} [props.indent]         extra left padding in px
 * @param {object} [props.sx]             extra sx merged onto the row
 */
export const SwitchRow = ({ label, checked = false, onChange, disabled = false, indent = 0, sx, ...rest }) => {
  const theme = useTheme();
  const { palette } = theme;

  return (
    <Box
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={() => {
        if (!disabled && onChange) onChange(!checked);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 14px',
        paddingLeft: `${14 + indent}px`,
        cursor: disabled ? 'default' : 'pointer',
        ...sx,
      }}
      {...rest}
    >
      <Box component="span" sx={{ flex: 1, color: palette.text.default, fontSize: '13px' }}>
        {label}
      </Box>
      <Switch checked={checked} disabled={disabled} sx={{ pointerEvents: 'none' }} />
    </Box>
  );
};
